import { Select, MenuItem } from "@mui/material";
import StatusBadge from "./StatusBadge";

/**
 * StageStepper — Phase 8 (Design Gap Audit item H) reusable component.
 *
 * Matches the prototype's inline stage-advance control on each candidate
 * row. Implemented as a compact Select styled to look like the status
 * badge itself (so it reads as a chip until clicked, then reveals every
 * valid stage) rather than a literal step-by-step "next stage" button —
 * this lets a user jump to any stage (e.g. straight to REJECTED), which
 * the existing app's Edit form already allows, so this preserves that
 * same flexibility rather than restricting it to a linear sequence.
 *
 * CRITICAL SAFETY NOTE — this component does NOT call any API itself. It
 * only reports the newly-selected value via `onChange`. The calling page
 * (Candidates.jsx) is responsible for sending the update as a FULL-OBJECT
 * PUT using the complete existing candidate record with only `feedback`
 * replaced — never a partial `{ feedback: newValue }` payload. See
 * Candidates.jsx's `onStageChange` for that implementation.
 *
 * Props:
 *   value    - current feedback value, one of the 8 existing backend enum
 *              values (required)
 *   onChange - (newValue) => void, called when a different stage is picked (required)
 *   disabled - optional, disables the control (e.g. while a save is in flight)
 *   toneMap  - { [feedbackValue]: toneKey } — reuses whatever tone mapping
 *              the calling page already uses for StatusBadge, so colors
 *              stay identical to the read-only badge elsewhere (required)
 *   labelMap - (feedbackValue) => display label — reuses the calling
 *              page's existing statusLabel() function (required)
 *   options  - array of the valid feedback values to offer, in display
 *              order (required — must be exactly the 8 existing backend
 *              enum values already in use: PENDING, SHORTLISTED, SELECTED,
 *              L1_ROUND, L2_ROUND, ON_HOLD, REJECTED, DUPLICATE; this
 *              component does not invent additional statuses)
 */
export default function StageStepper({
  value,
  onChange,
  disabled,
  toneMap,
  labelMap,
  options,
}) {
  return (
    <Select
      size="small"
      value={value}
      disabled={disabled}
      onChange={(e) => {
        if (e.target.value !== value) onChange(e.target.value);
      }}
      onClick={(e) => e.stopPropagation()}
      renderValue={(v) => (
        <StatusBadge label={labelMap(v)} tone={toneMap[v] || "neutral"} />
      )}
      sx={{
        "& .MuiSelect-select": { py: 0.25, px: 0.5 },
        "& fieldset": { border: "none" },
        minWidth: 0,
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
          {labelMap(opt)}
        </MenuItem>
      ))}
    </Select>
  );
}
